import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import _ from 'lodash'
import cx from 'classnames'

import LegendTable from '../../../../../../../../components/LegendTable'
import CustomDate from '../../../../../../../../components/CustomDate'
import GdprIcon from '../../../../../../../../components/GdprIcon'

import Terminology from './components/Terminology'
import ScoringResults from './components/ScoringResults'
import ScoringLabels from './components/ScoringLabels'
import ScoringChart from './components/Chart'
import Degrees from './components/Degrees'

import { LOCALE_PREFIX } from '../Blocks/BisnodeScoring/constants'

import { labels, NA_CODES } from './constants'

import './style.scss'

export default class BisnodeScoring extends Component {
    static propTypes = {
        block: PropTypes.string.isRequired,
        chartData: PropTypes.array,
        data: PropTypes.object,
        id: PropTypes.string.isRequired,
        legend: PropTypes.array,
        level: PropTypes.string.isRequired,
        module: PropTypes.string.isRequired,
        customData: PropTypes.object,
        saveCustomData: PropTypes.func,
        originalData: PropTypes.array,
        scoringDegrees: PropTypes.array,
        translates: PropTypes.shape({
            degreeLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            descriptionLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            probabilityLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            terminologyToggleText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            terminologyScoringText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            terminologyKreditText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            terminologyTransactionText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            terminologyLimitsText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            creditsLimitText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            transactionsLimitText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            criteriaText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            criteriaTitleText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            refreshText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            lastChangeText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            yearOfFinancialStatements: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
        }).isRequired,
    }

    getInterval = (legendItem, currentCode) => {
        if (!legendItem?.probabilityOfDefault || (currentCode === 'NQ' && legendItem.code === currentCode)) return ''

        const lowerBound = legendItem.probabilityOfDefault?.lowerBound
        const upperBound = legendItem.probabilityOfDefault?.upperBound

        if (typeof lowerBound !== 'number') return ''
        if (typeof upperBound !== 'number') return `${lowerBound} %`

        return `${lowerBound} - ${upperBound} %`
    }

    getSetup = () =>
        _.map(this.props.legend, (legendItem) => {
            const dataCode = NA_CODES.includes(this.props.data?.code) ? 'NQ' : this.props.data?.code
            const isActive = dataCode === legendItem.code

            return {
                ...legendItem,
                active: isActive,
                setup: [
                    { identifier: 'thermometer', header: { minWidth: '12px' } },
                    {
                        identifier: 'label',
                        arrow: true,
                        visibleDefault: !isActive,
                        style: {
                            minWidth: isActive ? '203px' : '180px',
                            color: legendItem.color,
                            fontWeight: isActive ? 900 : 'auto',
                            fontSize: isActive && '22px',
                            paddingLeft: !isActive && '25px',
                            borderRight: '1px solid #4d4d4d',
                        },
                        value: legendItem.degree,
                        header: {
                            label: this.props.translates.degreeLabel,
                            style: { lineHeight: '40px', minWidth: '180px', paddingLeft: '0px' },
                        },
                    },
                    {
                        identifier: 'label',
                        style: { borderRight: '1px solid #4d4d4d', width: '33.3%' },
                        value: isActive ? (
                            <div className={cx('description-active', dataCode)}>
                                <div>{legendItem.description}</div>
                                {dataCode !== 'NQ' && (
                                    <div>
                                        ({this.props.translates.lastChangeText}
                                        <CustomDate
                                            date={_.get(this.props.data, 'lastChangedDate.value')}
                                            precision={_.get(this.props.data, 'lastChangedDate.precision')}
                                        />
                                        )
                                    </div>
                                )}
                            </div>
                        ) : (
                            legendItem.description
                        ),
                        header: { label: this.props.translates.descriptionLabel, style: { lineHeight: '40px' } },
                    },
                    {
                        identifier: 'label',
                        style: {
                            flex: '1 0 auto',
                            fontWeight: isActive ? 600 : 'auto',
                            position: 'relative',
                            minWidth: '110px',
                        },
                        value: (
                            <div
                                style={{
                                    position: 'absolute',
                                    right: isActive ? '9%' : '5%',
                                    fontSize: isActive ? '24px' : 'auto',
                                }}
                            >
                                {isActive && dataCode !== 'NQ'
                                    ? `${_.get(this.props.data, 'probabilityOfDefault')}%`
                                    : this.getInterval(legendItem, dataCode)}
                            </div>
                        ),
                        header: { label: this.props.translates.probabilityLabel },
                    },
                ],
            }
        })

    render() {
        const {
            chartData,
            translates: {
                terminologyToggleText,
                terminologyScoringText,
                terminologyKreditText,
                terminologyTransactionText,
                terminologyLimitsText,
                creditsLimitText,
                transactionsLimitText,
                criteriaText,
                criteriaTitleText,
                refreshText,
                yearOfFinancialStatements,
            },
            data,
            module,
            block,
            id,
            legend,
            level,
            originalData,
            customData,
            saveCustomData,
            scoringDegrees,
        } = this.props

        const [parsedLevel] = level
            .split(',')
            .map((x) => parseInt(x, 10))
            .slice(-1)

        return (
            <div className={'bisnode-scoring'}>
                <div className={'gdrp-container'}>
                    <GdprIcon id={id} block={block}>
                        <FormattedMessage
                            id={`${LOCALE_PREFIX}.gdprTooltip`}
                            values={{
                                strong: (chunks) => <strong>{chunks}</strong>,
                                br: <br />,
                                acz: (chunks) => (
                                    <a
                                        href="mailto:podpora.cz@dnb.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#7fcdee', textDecoration: 'underline' }}
                                    >
                                        {chunks}
                                    </a>
                                ),
                                ask: (chunks) => (
                                    <a
                                        href="mailto:podpora.sk@dnb.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#7fcdee', textDecoration: 'underline' }}
                                    >
                                        {chunks}
                                    </a>
                                ),
                            }}
                        />
                    </GdprIcon>
                </div>

                <div className={'bisnode-scoring-head-information-wrap'}>
                    <div className={'bisnode-scoring-refresh-wrap'}>
                        <span>{refreshText}</span>
                        <CustomDate
                            date={_.get(data, 'refreshDate.value')}
                            precision={_.get(data, 'refreshDate.precision')}
                        />
                    </div>
                    {_.get(data, 'financialStatementsYear') && (
                        <div className={'bisnode-scoring-year-of-financial-statement-wrap'}>
                            <span>{yearOfFinancialStatements}</span>
                        </div>
                    )}
                </div>
                <div className={'bisnode-scoring-legend-wrap'}>
                    <LegendTable legend={this.getSetup()} />
                    <ScoringResults
                        data={data}
                        level={level}
                        translates={{ creditsLimitText, transactionsLimitText }}
                    />
                </div>
                <Terminology
                    translates={{
                        terminologyToggleText,
                        terminologyScoringText,
                        terminologyKreditText,
                        terminologyTransactionText,
                        terminologyLimitsText,
                    }}
                    customData={customData}
                    saveCustomData={saveCustomData}
                />
                {parsedLevel >= 20 && !_.isEmpty(scoringDegrees) && (
                    <>
                        <div className={'title-container'}>
                            <FormattedMessage id={`${LOCALE_PREFIX}.historyOfScoringLevels`} />
                        </div>
                        <div className={'scoring-degress-wrapper'}>
                            <Degrees module={module} block={block} id={id} legend={legend} data={scoringDegrees} />
                        </div>
                    </>
                )}
                {parsedLevel >= 30 && (
                    <div>
                        <div className={'title-container'}>
                            <FormattedMessage id={`${LOCALE_PREFIX}.evolutionOfProbabilityOfBankruptcy`} />
                        </div>
                        <ScoringChart
                            data={chartData}
                            originalData={originalData}
                            module={module}
                            level={level}
                            block={block}
                            id={id}
                            customData={customData}
                        />
                    </div>
                )}
                <ScoringLabels
                    labels={labels}
                    translates={{ criteriaText, criteriaTitleText }}
                    creditLimit={data?.creditLimit}
                    code={_.get(data, 'code', null)}
                />
            </div>
        )
    }
}
